import PostVideoPlayer from "@/components/Post/PostVideoPlayer";
import AuthContext from "@/context/auth";
import getStreamCDNPublicUrl from "@/libs/get-cdn-public-url";
import { postPlayerStore } from "@/libs/stores/post-player.store";
// @ts-ignore
import firebase from "@/libs/firebase";
import { getIdToken } from "firebase/auth";
import { useContext, useEffect, useState } from "react";

import {
  Box,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";

/**
 * Get image URL using proxy endpoint for clear content, fallback to blur via CDN
 * Same logic as in Card.jsx
 */
const getImageUrl = async (clearPath, blurPath, creatorId, user) => {
  // blurPath always exists, normalize it for fallback
  const normalizedBlurPath = blurPath.startsWith("/") ? blurPath.slice(1) : blurPath;
  const blurUrl = getStreamCDNPublicUrl(normalizedBlurPath);

  // If no clear path, use blur
  if (!clearPath) {
    return blurUrl;
  }

  const mediaApiBaseUrl = import.meta.env.VITE_MEDIA_PROXY_SERVER;
  if (!mediaApiBaseUrl) {
    // If no proxy server, use blur from CDN
    return blurUrl;
  }

  // Get auth token if user is logged in
  let token = null;
  if (user) {
    try {
      const currentUser = firebase.auth?.currentUser;
      if (currentUser) {
        token = await getIdToken(currentUser);
      }
    } catch (error) {
      // Failed to get token, will try without token
    }
  }

  // Build proxy URL for clear content
  const mediaPath = clearPath.startsWith("/") ? clearPath : `/${clearPath}`;
  let proxyUrl = creatorId
    ? `${mediaApiBaseUrl}/media/proxy?path=${encodeURIComponent(mediaPath)}&creatorId=${creatorId}`
    : `${mediaApiBaseUrl}/media/proxy?path=${encodeURIComponent(mediaPath)}`;

  // Append token as query parameter if available
  if (token) {
    proxyUrl += `&token=${encodeURIComponent(token)}`;
  }

  // Try to access clear content via proxy
  try {
    const response = await fetch(proxyUrl, {
      method: "HEAD",
    });

    // If 403 (access denied) or any error, use blur from CDN
    if (response.status === 403 || !response.ok) {
      return blurUrl;
    }

    // If successful (200, 206, etc.), use proxy URL for clear content
    return proxyUrl;
  } catch (error) {
    // On error, fallback to blur from CDN
    return blurUrl;
  }
};

export default function PostLightbox({
  assets = [],
  isOpen,
  onClose,
  startIndex = 0,
  creatorId,
}) {
  const { user } = useContext(AuthContext);
  const [activeIndex, setActiveIndex] = useState(startIndex);
  const [resolvedImageUrls, setResolvedImageUrls] = useState({});

  // Resolve image URLs asynchronously (same logic as Card.jsx)
  useEffect(() => {
    if (!isOpen) return;
    
    const resolveImageUrls = async () => {
      const newUrls = {};

      for (let i = 0; i < assets.length; i++) {
        const asset = assets[i];
        if (asset?.type === "image" && asset.clear) {
          const url = await getImageUrl(asset.clear, asset.blur || asset.clear, creatorId, user);
          newUrls[i] = url;
        }
      }

      setResolvedImageUrls(newUrls);
    };

    resolveImageUrls();
  }, [isOpen, assets, creatorId, user]);

  // Load initial video when lightbox opens
  useEffect(() => {
    if (isOpen && assets.length > 0) {
      const initialAsset = assets[startIndex];
      if (initialAsset?.type === "video") {
        setActiveIndex(startIndex);
        // Load will be triggered by PostVideoPlayer when it becomes active
      }
    } else if (!isOpen) {
      // Unload when lightbox closes
      postPlayerStore.unload();
      setResolvedImageUrls({});
    }
  }, [isOpen, startIndex, assets]);

  const handleSlideChange = (swiper) => {
    const newIndex = swiper.activeIndex;
    setActiveIndex(newIndex);
    
    // Unload previous video and load new one
    const activeAsset = assets[newIndex];
    if (activeAsset?.type === "video") {
      const assetWithCreatorId = {
        ...activeAsset,
        creatorId: activeAsset.creatorId || creatorId,
      };
      postPlayerStore.load(assetWithCreatorId);
    } else {
      // If switching to image, unload video
      postPlayerStore.unload();
    }
  };

  const handleClose = () => {
    postPlayerStore.unload();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full" isCentered>
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="black" rounded="0" maxW="100vw" h="100vh">
        <ModalBody p={0} position="relative">
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            position="absolute"
            top={3}
            right={3}
            zIndex={10}
            rounded="full"
            bg="whiteAlpha.200"
            color="white"
            _hover={{ bg: "whiteAlpha.300" }}
            _active={{ bg: "whiteAlpha.400" }}
            icon={<span style={{ fontWeight: 800, fontSize: 18 }}>✕</span>}
          />
          <Swiper 
            initialSlide={startIndex} 
            slidesPerView={1} 
            spaceBetween={12}
            onSlideChange={handleSlideChange}
          >
            {assets.map((asset, idx) => {
              if (asset?.type === "video") {
                const isActive = idx === activeIndex;
                const assetWithCreatorId = {
                  ...asset,
                  creatorId: asset.creatorId || creatorId,
                };
                return (
                  <SwiperSlide key={asset.id || idx}>
                    <Box
                      w="100%"
                      h="100vh"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg="black"
                    >
                      <Box
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <PostVideoPlayer
                          asset={assetWithCreatorId}
                          poster={getStreamCDNPublicUrl(asset.thumbnail?.clear) || undefined}
                          controls
                          autoplay={isActive}
                          muted={false}
                          isActive={isActive}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    </Box>
                  </SwiperSlide>
                );
              }
              
              // Image asset
              // Get resolved URL if available, otherwise use blur as fallback
              const resolvedUrl = resolvedImageUrls[idx];
              const blurPath = asset.blur || asset.clear;
              const blurUrl = blurPath ? getStreamCDNPublicUrl(blurPath.startsWith("/") ? blurPath.slice(1) : blurPath) : null;
              const src = resolvedUrl || blurUrl || getStreamCDNPublicUrl(asset.clear);
              
              if (!src) return null;
              
              return (
                <SwiperSlide key={asset.id || idx}>
                  <Box
                    w="100%"
                    h="100vh"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="black"
                    position="relative"
                  >
                    {/* Blurred background image (same as Card.jsx) */}
                    {blurUrl && (
                      <Box
                        position="absolute"
                        inset={0}
                        backgroundImage={`url(${blurUrl})`}
                        backgroundSize="cover"
                        backgroundPosition="center"
                        backgroundRepeat="no-repeat"
                        filter="blur(20px)"
                        transform="scale(1.1)"
                        zIndex={0}
                      />
                    )}
                    {/* Actual image */}
                    <Box
                      position="relative"
                      zIndex={1}
                      w="100%"
                      h="100%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <img
                        src={src}
                        alt=""
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  </Box>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}


